import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, COMPANY } from "../lib/site";
import { useLang } from "../i18n/LanguageContext";
import { LANGUAGES } from "../i18n/translations";

const SITE_ORIGIN = () => (typeof window !== "undefined" ? window.location.origin : "");

function ensureMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
function ensureLink(rel, href, extra = {}) {
  const sel = `link[rel="${rel}"]${Object.entries(extra).map(([k,v]) => `[${k}="${v}"]`).join("")}`;
  let el = document.head.querySelector(sel);
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); Object.entries(extra).forEach(([k,v]) => el.setAttribute(k, v)); document.head.appendChild(el); }
  el.setAttribute("href", href);
}
function removeById(id) { const el = document.getElementById(id); if (el) el.remove(); }
function upsertScript(id, json) {
  removeById(id);
  const s = document.createElement("script"); s.type = "application/ld+json"; s.id = id; s.textContent = JSON.stringify(json);
  document.head.appendChild(s);
}

export default function Seo({ title, description, path = "/", image, noindex = false, type = "website" }) {
  const { lang } = useLang();
  const location = useLocation();
  const { data: gen } = useQuery({ queryKey: ["settings","general","seo"], queryFn: async () => (await api.get("/settings/general")).data, staleTime: 300000 });

  useEffect(() => {
    const origin = SITE_ORIGIN();
    const url = origin + (path || location.pathname);
    if (title) document.title = title;
    ensureMeta("description", description);
    ensureMeta("robots", noindex ? "noindex, follow" : "index, follow");
    ensureMeta("og:title", title, "property");
    ensureMeta("og:description", description, "property");
    ensureMeta("og:type", type, "property");
    ensureMeta("og:url", url, "property");
    ensureMeta("og:site_name", "NOUROZI EBYGG & ANLEGG", "property");
    ensureMeta("og:locale", lang === "no" ? "no_NO" : lang === "en" ? "en_US" : lang === "de" ? "de_DE" : lang === "ru" ? "ru_RU" : lang === "fa" ? "fa_IR" : "no_NO", "property");
    if (image) ensureMeta("og:image", image, "property");
    ensureMeta("twitter:card", image ? "summary_large_image" : "summary");
    ensureMeta("twitter:title", title);
    ensureMeta("twitter:description", description);
    if (image) ensureMeta("twitter:image", image);

    ensureLink("canonical", url);

    // hreflang alternates
    document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
    LANGUAGES.forEach(l => {
      const link = document.createElement("link");
      link.setAttribute("rel", "alternate"); link.setAttribute("hreflang", l.code === "no" ? "nb-NO" : l.code);
      link.setAttribute("href", url);
      document.head.appendChild(link);
    });
    const xdefault = document.createElement("link");
    xdefault.setAttribute("rel", "alternate"); xdefault.setAttribute("hreflang", "x-default");
    xdefault.setAttribute("href", url);
    document.head.appendChild(xdefault);

    // Google Search Console verification tag from settings
    const gsc = gen?.value?.google_verification;
    if (gsc) ensureMeta("google-site-verification", gsc);

    // LocalBusiness schema (once)
    upsertScript("ld-localbusiness", {
      "@context": "https://schema.org", "@type": "GeneralContractor",
      "name": "NOUROZI EBYGG & ANLEGG",
      "telephone": "+47 " + COMPANY.phone,
      "email": gen?.value?.email || COMPANY.email,
      "url": origin,
      "areaServed": { "@type": "City", "name": "Halden" },
      "address": { "@type": "PostalAddress", "addressLocality": "Halden", "addressCountry": "NO" },
      "sameAs": [gen?.value?.facebook, gen?.value?.instagram, gen?.value?.google_business_url].filter(Boolean),
      "description": "Tømrer- og byggmesterbedrift i Halden. Tømrerarbeid, oppussing, kjøkken, terrasse, tak, kledning, belegningsstein, graving, dører/vinduer og tilbygg.",
      "priceRange": "$$",
    });

    // BreadcrumbList
    const parts = (path || location.pathname).split("/").filter(Boolean);
    const crumbs = [{ "@type": "ListItem", position: 1, name: "Hjem", item: origin + "/" }];
    parts.forEach((p, i) => crumbs.push({ "@type": "ListItem", position: i + 2, name: decodeURIComponent(p).replace(/-/g, " "), item: origin + "/" + parts.slice(0, i + 1).join("/") }));
    upsertScript("ld-breadcrumb", { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: crumbs });
  }, [title, description, path, image, noindex, type, lang, location.pathname, gen]);

  return null;
}
